import { useCallback } from 'react';
import { STRIPE_PRODUCT_IDS } from '@cx-web/repub-config';

import { type MetaPixelEventName, type MetaPixelSubscriptionClickParams } from '../types/meta-pixel-events';

interface UseTrackGZMetaPixelSubscriptionClickConfig {
  ctaName?: string;
  externalUrl?: string;
  productId: string;
}

interface UrlParams {
  offerId: string | null;
  plan: string | null;
}

function extractParamsFromUrl(url: string): UrlParams {
  try {
    const urlObj = new URL(url);
    return {
      offerId: urlObj.searchParams.get('OfferId'),
      plan: urlObj.searchParams.get('pwcmp'),
    };
  } catch {
    return { offerId: null, plan: null };
  }
}

interface ProductMetadata {
  billing_model: 'monthly' | 'quarterly_after_trial';
  product_type: 'paid_subscription' | 'free_trial';
  trial_days?: number;
}

function getProductMetadata(productId: string): ProductMetadata | undefined {
  if (productId === STRIPE_PRODUCT_IDS.GAZZETTA_PLUS_MONTHLY) {
    return {
      billing_model: 'monthly',
      product_type: 'paid_subscription',
    };
  }
  if (productId === STRIPE_PRODUCT_IDS.GAZZETTA_PLUS_THREE_MONTHLY) {
    return {
      billing_model: 'quarterly_after_trial',
      product_type: 'free_trial',
      trial_days: 14,
    };
  }
  return undefined;
}

/**
 * React hook that creates a tracking function for GZ Meta Pixel subscription click events.
 */
export function useTrackGZMetaPixelSubscriptionClick(config: UseTrackGZMetaPixelSubscriptionClickConfig): () => void {
  const { ctaName, externalUrl, productId } = config;

  const trackClick = useCallback(() => {
    // Check if Meta Pixel is loaded
    if (!window.fbq) {
      return;
    }

    // Check consent
    if (!window.iframeGZContext?.consentGranted) {
      return;
    }

    // Check if external URL exists
    if (!externalUrl) {
      return;
    }

    // Extract required parameters from URL (single parse)
    const { offerId, plan } = extractParamsFromUrl(externalUrl);

    // Don't fire event if required parameters are missing
    if (!offerId || !plan) {
      return;
    }

    // Get product metadata (billing model, product type, trial days)
    const productMetadata = getProductMetadata(productId);

    // Build event parameters
    const params: MetaPixelSubscriptionClickParams = {
      offer_id: offerId,
      plan,
      funnel_name: 'AI Predictor',
      landing_name: 'AI Predictor',
      landing_url: window.location.href,
      product_type: productMetadata?.product_type ?? 'paid_subscription',
    };

    // Add optional parameters
    if (ctaName) {
      params.cta_name = ctaName;
    }

    if (productMetadata?.billing_model) {
      params.billing_model = productMetadata.billing_model;
    }

    if (productMetadata?.trial_days !== undefined) {
      params.trial_days = productMetadata.trial_days;
    }

    // Fire Meta Pixel custom event
    const eventName: MetaPixelEventName = 'ClickSubscribeCTA';
    window.fbq('trackCustom', eventName, params);
  }, [productId, externalUrl, ctaName]);

  return trackClick;
}
